import { Http } from '@/lib/http';
import LoanMangementService from '@/modules/loanpro/services/loan-management.service';
import { UniversaIntegrationService } from './uniserva-integration.service';
import AccountManagementService from '@/modules/loanpro/services/account-management.service';
import { IntegrationConfigService } from '@/modules/integrations/services/integration-config.service.js';

//Description type that describe where the record was found
const FoundType = Object.freeze({
  CORE: 'core',
  LOCAL: 'local'
});

export class CoreIntegrationService extends Http {
  constructor() {
    super();
    this.endPoint = 'integration/core';
    this.loanManagementService = new LoanMangementService();
    this.accountManagementService = new AccountManagementService();
    this.universaIntegrationService = new UniversaIntegrationService();
  }

  /**
   * Get core status information such as the if an integration is active and the name
   * @returns { { isIntegrated: Boolean, core: string } } core status
   */
  async getCoreIntegratedStatus() {
    const integrationConfigService = new IntegrationConfigService();
    const { data: integrationConfig } = await integrationConfigService.find();
    return {
      isIntegrated: !!integrationConfig?.coreSystems?.selected,
      core: integrationConfig?.coreSystems?.selected
    };
  }

  /**
   * Lookup account details
   * @param { {account: Object, persons: Array<Object> } }
   * @throws HttpError
   * @returns { Array<Objects> } list of persons
   */
  async findAccountDetails({ account, persons }) {
    const integrationConfigService = new IntegrationConfigService();
    const { data: integrationConfig } = await integrationConfigService.find();

    let hasMultipleAccounts = false;
    let accounts = [];

    //check if system is allowed to update local account information from the core
    const canUpdateAccountFromCore =
      integrationConfig?.coreSystems?.universa?.config?.features?.some(
        (el) => el.toLowerCase() === 'updateOnSearch'
      );

    //Lookup person on universa if it was selected in settings
    if (
      integrationConfig?.coreSystems?.selected === 'Universa' &&
      canUpdateAccountFromCore
    ) {
      if (account?.id) {
        const { data } = await this.accountManagementService.getAccountById(
          account.id
        );
        account = data.account;
        persons = data.persons;
      }

      //update all persons on the account if they are found on the core
      let personAccounts = [];
      for (const p of persons) {
        const res =
          await this.universaIntegrationService.findAndUpdatePersonRecord(p);

        if (res && res?.length > 0) {
          personAccounts = res;
        }
      }

      //merge local account with information from core
      const index = personAccounts.findIndex(
        (e) => account?.mainAccountNumber == e.mainAccountNumber
      );
      if (index > -1) {
        personAccounts.splice(index, 1, account);
      }

      if (personAccounts.length > 1) {
        hasMultipleAccounts = true;
        accounts = personAccounts;
      }
    } else {
      //Lookup person locally if no core was selected in settings
      const { data } = await this.accountManagementService.getAccountById(
        account.id
      );
      account = data.account;
      persons = data.persons;
    }

    return { account, persons, hasMultipleAccounts, accounts };
  }

  /**
   * Lookup person locally or on core if core is active
   * @param { {trn: string, firstName: string, lastName: string, dateOfBirth: string, mainAccountNumber: string, limit: number}} search paramaters
   * @throws HttpError
   * @returns { Array<Objects> } list of persons
   */
  async findPersons({
    trn,
    firstName,
    lastName,
    dateOfBirth,
    mainAccountNumber,
    limit
  }) {
    const integrationConfigService = new IntegrationConfigService();
    const { data: integrationConfig } = await integrationConfigService.find();

    if (integrationConfig?.coreSystems?.selected === 'Universa') {
      const requests = [
        this.accountManagementService.search({
          trn,
          firstName,
          lastName,
          dateOfBirth,
          mainAccountNumber
        })
      ];

      //check if system is allowed to search the core
      const canSearchCore =
        integrationConfig?.coreSystems?.universa?.config?.features?.some(
          (el) => el.toLowerCase() === 'search'
        );
      if (canSearchCore) {
        requests.push(
          this.universaIntegrationService.searchPerson({
            firstName,
            lastName,
            limit
          })
        );
      }
      const [{ data: localRes }, coreResponse] = await Promise.all(requests);

      //format data coming from universa

      let localPersons = [];
      let corePersons = [];

      localPersons = localRes.data.map((el) => {
        el.foundOn = FoundType.LOCAL;
        return el;
      });

      corePersons =
        coreResponse?.data?.data?.persons
          .filter(
            (el) =>
              !localPersons.some((e) =>
                e.persons.some((e) => el.core_id === e.coreId)
              )
          )
          .map((el) => {
            return {
              foundOn: FoundType.CORE,
              ...UniversaIntegrationService.formatPersonRecord(el)
            };
          }) || [];

      const allPersons = localPersons.concat(corePersons);
      return allPersons;
    }

    const { data: localRes } = await this.accountManagementService.search({
      trn,
      firstName,
      lastName,
      dateOfBirth,
      mainAccountNumber
    });

    return localRes.data.map((el) => {
      el.foundOn = FoundType.LOCAL;
      return el;
    });
  }

  /**
   * getPersonLoans get all disbursed loans belonging to a person
   * @param { string } coreId - person unique core id
   * @throws HttpError
   */
  async getPersonLoans(coreId) {
    const integrationConfigService = new IntegrationConfigService();
    const { data: integrationConfig } = await integrationConfigService.find();

    const canUpdateLoan =
      integrationConfig?.coreSystems?.universa?.config?.features?.some(
        (el) => el.toLowerCase() === 'updateLoanBalancesPerAccount'
      );

    if (
      integrationConfig?.coreSystems?.selected === 'Universa' &&
      canUpdateLoan
    ) {
      return this.universaIntegrationService.getPersonLoans(coreId);
    }

    return { data: { data: { loans: [] } } };
  }

  /**
   * createLoan create loan on core
   * @param {object} loan
   * @throws HttpError
   */
  async createLoan(loan) {
    const integrationConfigService = new IntegrationConfigService();
    const { data: integrationConfig } = await integrationConfigService.find();

    if (integrationConfig?.coreSystems?.selected === 'Universa') {
      return this.universaIntegrationService.createLoan(loan);
    }
  }
}
